/**
 * useDetailNavMenu — Composable for floating detail page navigation menu
 *
 * Manages:
 * - Open/close state with localStorage persistence
 * - Left/right side position with localStorage persistence
 * - First-visit detection (starts closed on first ever visit)
 * - Drag logic for repositioning
 *
 * @version 1.0.0
 */

import { ref, onMounted } from 'vue'

const STORAGE_KEY = 'gg-detail-nav-menu'

interface NavMenuPrefs {
  side: 'left' | 'right'
  open: boolean
  visited: boolean
}

function loadPrefs(): NavMenuPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { side: 'left', open: false, visited: false }
}

function savePrefs(prefs: NavMenuPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch { /* ignore */ }
}

export interface DetailNavItem {
  /** Unique key (used as scroll target id) */
  id: string
  /** Material Symbol icon name */
  icon: string
  /** Tooltip / aria-label */
  label: string
}

export function useDetailNavMenu() {
  const isOpen = ref(false)
  const side = ref<'left' | 'right'>('left')
  const isDragging = ref(false)
  const dragPreviewSide = ref<'left' | 'right' | null>(null)

  onMounted(() => {
    const prefs = loadPrefs()
    side.value = prefs.side
    isOpen.value = prefs.visited ? prefs.open : false
    // Mark as visited
    if (!prefs.visited) {
      savePrefs({ ...prefs, visited: true })
    }
  })

  function toggle() {
    isOpen.value = !isOpen.value
    persist()
  }

  function setSide(newSide: 'left' | 'right') {
    side.value = newSide
    persist()
  }

  function persist() {
    savePrefs({ side: side.value, open: isOpen.value, visited: true })
  }

  function scrollToSection(sectionId: string) {
    const el = document.getElementById(sectionId)
    if (!el) return

    // Walk up to find the actual scrollable ancestor
    let scrollEl: Element | null = el.parentElement
    while (scrollEl && scrollEl !== document.documentElement) {
      const style = getComputedStyle(scrollEl)
      const overflowY = style.overflowY
      if ((overflowY === 'auto' || overflowY === 'scroll') && scrollEl.scrollHeight > scrollEl.clientHeight) {
        break
      }
      scrollEl = scrollEl.parentElement
    }
    if (!scrollEl) scrollEl = document.documentElement

    const containerRect = scrollEl === document.documentElement ? { top: 0 } : scrollEl.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const targetY = scrollEl.scrollTop + elRect.top - containerRect.top - 8
    const startY = scrollEl.scrollTop
    const diff = targetY - startY
    if (Math.abs(diff) < 1) return

    const duration = 500
    let start: number | null = null

    function easeOutCubic(t: number): number {
      return 1 - Math.pow(1 - t, 3)
    }

    function step(timestamp: number) {
      if (start === null) start = timestamp
      const progress = Math.min((timestamp - start) / duration, 1)
      scrollEl!.scrollTop = startY + diff * easeOutCubic(progress)
      if (progress < 1) requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
  }

  return {
    isOpen,
    side,
    isDragging,
    dragPreviewSide,
    toggle,
    setSide,
    persist,
    scrollToSection,
  }
}
